import React, { useMemo, useRef } from "react";
import { Button, Modal, Row, Typography, Form, FormInstance, Col, Input, Select, Space, Table, InputNumber } from "antd";
import { cloneDeep, isNil } from "lodash";
import CustomInputNumber from "src/components/AntdCustomPart/InputNumber/CustomInputNumber";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import {
  AttributeManualFlareMonthly,
  ManualFlareMonthly,
  ResponseManualFlareVolume,
  ResponseManualFlareVolumeForEditor,
} from "src/entity/flare/manual-flare-volume.entity";
import { ColumnGroupType } from "antd/es/table";
import { ColumnType } from "antd/es/list";
import { getFlareCaseDisplayLabel } from "src/entity/flare/flare.constants";
import { capitalizeFirstLetter } from "src/utils/manage-string";
import "./ManualVolumeModal.css";
import { ReportStatusEnum } from "src/entity/report-voc-emission/report-voc-emission.entity";
import { valueType } from "antd/es/statistic/utils";
import { onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";
import { formatNumber } from "src/utils/process-number";
import { KeyofMonthly } from "src/entity/common/global.entity";

const { Text } = Typography;

interface ManualVolumeModalProps {
  submitSetupHeatingValue: () => void;
  loadingManageFlareVolume: boolean;
  visibleModal: boolean;
  closeModal: () => void;
  error: string | null;
  isEdit: ResponseManualFlareVolumeForEditor | null;
  setUpManualVolume: (month: keyof ManualFlareMonthly, type: keyof AttributeManualFlareMonthly, value: number | string) => void;
  getMasterLabel: (type: string, text: string) => string | undefined;
}
interface RecordProps {
  key: number;
  type: keyof AttributeManualFlareMonthly;
}

const ManualVolumeModal: React.FC<ManualVolumeModalProps> = (props) => {
  type MonthManual = keyof ManualFlareMonthly;
  const months: MonthManual[] = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

  const { submitSetupHeatingValue, loadingManageFlareVolume, closeModal, visibleModal, isEdit, setUpManualVolume, getMasterLabel } = props;

  // console.log("IS edit : ", isEdit);
  const twoPointDecimal = (number: valueType | null) => Number(number).toFixed(2);
  const getColumns = () => {
    return months.map((item) => ({
      title: capitalizeFirstLetter(item),
      key: item,
      align: "center" as const,
      render: (_: any, record: RecordProps) => (
        <InputNumber
          onChange={(val) => setUpManualVolume(item, record.type, (val as string | number) || 0)}
          className="w-16"
          value={isEdit?.manualFlareVolume && isEdit?.manualFlareVolume[record.type][item as keyof KeyofMonthly].value}
          disabled={
            isEdit?.manualFlareVolume &&
            isEdit?.manualFlareVolume[record.type][item as keyof KeyofMonthly].reportStatus !== ReportStatusEnum.PROCESSING
          }
          min={0}
          step={1}
          onKeyDown={(e) => {
            onKeyDownNumberWithFixedDecimal(e, 2);
          }}
          formatter={(value) => formatNumber(value as number, 2)}
        />
      ),
    }));
  };

  return (
    <Modal
      title={<ModalTitle title="Flare Volume" />}
      open={visibleModal}
      width={1100}
      onCancel={closeModal}
      footer={
        <Row className="w-full border-t" justify={"end"}>
          <Button className="mt-4" key="submit" type="primary" loading={loadingManageFlareVolume} onClick={submitSetupHeatingValue}>
            Save
          </Button>
        </Row>
      }
      maskClosable={false}
      centered
    >
      <Col span={24}>
        <Row className="w-full mt-4" justify={"center"} gutter={[16, 0]}>
          <Col>
            <div>
              <Text>Source Name</Text>
            </div>
            <Input className="mt-2" disabled value={isEdit?.sourceName || ""} />
          </Col>
          <Col>
            <div>
              <Text>Gas Name</Text>
            </div>
            <Input className="mt-2" disabled value={getMasterLabel("gasName", isEdit?.gasName as string)} />
          </Col>
          <Col>
            <div>
              <Text>Area</Text>
            </div>
            <Input className="mt-2" disabled value={getMasterLabel("area", isEdit?.area as string)} />
          </Col>
          <Col>
            <div>
              <Text>Flare Name</Text>
            </div>
            <Input className="mt-2" disabled value={getMasterLabel("flareName", isEdit?.flareName as string)} />
          </Col>
          <Col>
            <div>
              <Text>Unit</Text>
            </div>
            <Input className="mt-2" disabled value={getMasterLabel("unit", isEdit?.gasName as string)} />
          </Col>
        </Row>

        <Row className="w-full mt-4" id={"wrap-manual-volume-table-config"}>
          <Table
            columns={[
              {
                title: "",
                dataIndex: "type",
                key: "type",
                render: (text) => <Text>{getFlareCaseDisplayLabel(text)}</Text>,
              },
              ...getColumns(),
            ]}
            scroll={{ x: "max-content" }}
            size={"small"}
            dataSource={[
              { key: 1, type: "normalOperate" },
              { key: 2, type: "turnaround" },
              { key: 3, type: "emergency" },
            ]}
            pagination={false}
          />
        </Row>
      </Col>
    </Modal>
  );
};

export default ManualVolumeModal;
